import Alpine from 'alpinejs';
import axios from '../../config/axios.js';

const toast = (msg, type = 'info') => window.toast?.(msg, type);

function inkoTarangTool(cfg = {}) {
  return {
    cfg,
    access: null,
    loadingAccess: true,
    uploading: false,
    processing: false,
    polling: false,
    preparingPdf: false,
    job: null,
    scale: cfg.allowed_scales?.[0] ?? 2,
    targetMode: 'scale',
    targetModes: [
      { id: 'scale', label: 'Scale (2×–8×)' },
      { id: 'custom', label: 'Ukuran Custom' },
      { id: 'dpi', label: 'Target DPI' },
    ],
    customWidth: null,
    customHeight: null,
    targetDpi: cfg.default_dpi ?? 300,
    processingMode: 'balanced',
    pdfPage: 1,
    pdfMode: 'single',
    selectedPdfPages: [],
    convertPoints: cfg.points_per_credit ?? 100,
    converting: false,
    dragOver: false,
    comparePos: 50,
    fullscreenOpen: false,

    async init() {
      const isStaffPanel = this.cfg.context === 'kasir';
      const auth = Alpine.store('auth');

      if (!isStaffPanel && auth && !auth.isLoggedIn) {
        window.location.href = '/login?redirect=' + encodeURIComponent('/dashboard/inko-tarang');
        return;
      }
      if (auth?.ready && !isStaffPanel) {
        try { await auth.ready(); } catch { /* ignore */ }
      }
      await this.fetchAccess();
    },

    async fetchAccess() {
      this.loadingAccess = true;
      try {
        const { data } =         await axios.get('/api/v1/inko-tarang/access', {
          params: { credit_tier: this._baseCreditTier() },
        });
        this.access = data.data;
      } catch (e) {
        toast(e?.response?.data?.message || 'Gagal memuat akses.', 'error');
      } finally {
        this.loadingAccess = false;
      }
    },

    async convertCredits() {
      this.converting = true;
      try {
        const { data } = await axios.post('/api/v1/inko-tarang/credits/convert', { points: this.convertPoints });
        toast(data.message || 'Kredit ditambahkan.', 'success');
        await this.fetchAccess();
      } catch (e) {
        toast(e?.response?.data?.message || 'Konversi gagal.', 'error');
      } finally {
        this.converting = false;
      }
    },

    onFileSelect(e) {
      const file = e.target.files?.[0];
      if (file) this.uploadFile(file);
    },

    onDrop(e) {
      this.dragOver = false;
      const file = e.dataTransfer?.files?.[0];
      if (file) this.uploadFile(file);
    },

    async uploadFile(file) {
      this.uploading = true;
      this.job = null;
      this.pdfPage = 1;
      this.pdfMode = 'single';
      this.selectedPdfPages = [];
      const fd = new FormData();
      fd.append('file', file);
      try {
        const { data } = await axios.post('/api/v1/inko-tarang/upload', fd);
        this.job = data.data;
        if (this.job.original?.is_pdf) {
          this.pdfPage = this.job.original.pdf_page || 1;
        }
        toast('File berhasil diunggah.', 'success');
        await this.fetchAccess();
      } catch (e) {
        toast(e?.response?.data?.message || 'Upload gagal.', 'error');
      } finally {
        this.uploading = false;
      }
    },

    async loadPdfPage() {
      if (!this.job?.id || !this.job.original?.is_pdf) return;
      this.preparingPdf = true;
      try {
        const { data } = await axios.post(`/api/v1/inko-tarang/${this.job.id}/pdf-page`, {
          page: this.pdfPage,
        });
        this.job = data.data;
        toast('Preview halaman PDF siap.', 'success');
        await this.fetchAccess();
      } catch (e) {
        toast(e?.response?.data?.message || 'Gagal merender halaman PDF.', 'error');
      } finally {
        this.preparingPdf = false;
      }
    },

    canProcess() {
      if (this.job?.original?.is_pdf) {
        if (this.pdfMode === 'single' && !this.job.original?.width) {
          return false;
        }
        if (this.pdfMode === 'selected' && this.selectedPdfPages.length === 0) {
          return false;
        }
      }
      if (this.targetMode === 'custom' && !this.customWidth && !this.customHeight) {
        return false;
      }
      return true;
    },

    pdfPageOptions() {
      const count = this.job?.original?.pdf_page_count || 1;
      return Array.from({ length: count }, (_, i) => i + 1);
    },

    togglePdfPage(page) {
      const idx = this.selectedPdfPages.indexOf(page);
      if (idx >= 0) {
        this.selectedPdfPages.splice(idx, 1);
      } else {
        this.selectedPdfPages.push(page);
        this.selectedPdfPages.sort((a, b) => a - b);
      }
      this.fetchAccess();
    },

    _baseCreditTier() {
      if (this.targetMode === 'scale') {
        return this.scale;
      }
      return this.creditTier();
    },

    buildProcessPayload() {
      const payload = {
        mode: this.targetMode,
        processing_mode: this.processingMode,
      };

      if (this.job?.original?.is_pdf) {
        payload.pdf_mode = this.pdfMode;
        if (this.pdfMode === 'single') {
          payload.pdf_page = this.pdfPage;
        } else if (this.pdfMode === 'selected') {
          payload.pdf_pages = [...this.selectedPdfPages];
        }
      }

      if (this.targetMode === 'scale') {
        payload.scale = this.scale;
      } else if (this.targetMode === 'custom') {
        if (this.customWidth) payload.target_width = this.customWidth;
        if (this.customHeight) payload.target_height = this.customHeight;
      } else if (this.targetMode === 'dpi') {
        payload.target_dpi = this.targetDpi;
      }

      return payload;
    },

    async startProcess() {
      if (!this.job?.id || !this.canProcess()) return;

      if (this.job.original?.is_pdf && this.pdfMode === 'single' && !this.job.original?.width) {
        await this.loadPdfPage();
        if (!this.job.original?.width) return;
      }

      this.processing = true;
      try {
        const { data } = await axios.post(
          `/api/v1/inko-tarang/${this.job.id}/process`,
          this.buildProcessPayload(),
        );
        this.job = data.data;
        toast('Memproses scale-up…', 'info');
        await this.pollJob();
      } catch (e) {
        toast(e?.response?.data?.message || 'Gagal memulai proses.', 'error');
      } finally {
        this.processing = false;
      }
    },

    async pollJob() {
      if (!this.job?.id) return;
      this.polling = true;
      const maxAttempts = 120;
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        try {
          const { data } = await axios.get(`/api/v1/inko-tarang/${this.job.id}`);
          this.job = data.data;
          if (['completed', 'failed'].includes(this.job.status)) {
            if (this.job.status === 'completed') toast('Scale-up selesai!', 'success');
            else toast(this.job.error_message || 'Proses gagal.', 'error');
            await this.fetchAccess();
            break;
          }
        } catch {
          break;
        }
      }
      this.polling = false;
    },

    isProcessing() {
      if (!this.job) return false;
      return !['uploaded', 'completed', 'failed', 'expired'].includes(this.job.status);
    },

    statusLabel() {
      const map = {
        validating: 'Memvalidasi file…',
        processing: 'Menyiapkan proses…',
        analyzing: 'Menganalisis gambar…',
        upscaling: 'Menaikkan resolusi…',
        generating_preview: 'Membuat preview…',
      };
      return map[this.job?.status] || 'Memproses…';
    },

    creditTier() {
      const dims = this.resolveTargetDimensions();
      if (!dims) return this.scale;

      const { ow, oh, tw, th } = dims;
      const ratio = Math.max(tw / ow, th / oh);
      const allowed = this.cfg.allowed_scales || [2, 4, 8];

      for (const tier of allowed) {
        if (ratio <= tier + 0.01) return tier;
      }

      return allowed[allowed.length - 1] ?? 8;
    },

    creditCost() {
      const tier = this._baseCreditTier();
      let cost = this.cfg.credit_by_scale?.[tier]
        ?? this.access?.credit_costs?.[tier]
        ?? this.cfg.credit_by_scale?.[2]
        ?? 10;
      if (this.job?.original?.is_pdf
        && this.cfg.credit_calculation_mode === 'per_page'
        && ['all', 'selected'].includes(this.pdfMode)) {
        const pages = this.pdfMode === 'all'
          ? (this.job.original.pdf_page_count || 1)
          : Math.max(1, this.selectedPdfPages.length);
        cost *= pages;
      }
      return cost;
    },

    resolveTargetDimensions() {
      const orig = this.job?.original;
      if (!orig?.width || !orig?.height) return null;

      const ow = orig.width;
      const oh = orig.height;
      let tw;
      let th;

      if (this.targetMode === 'scale') {
        tw = Math.round(ow * this.scale);
        th = Math.round(oh * this.scale);
      } else if (this.targetMode === 'custom') {
        const aspect = ow / oh;
        if (this.customWidth) {
          tw = this.customWidth;
          th = Math.round(tw / aspect);
        } else if (this.customHeight) {
          th = this.customHeight;
          tw = Math.round(th * aspect);
        } else {
          return null;
        }
      } else if (this.targetMode === 'dpi') {
        const origDpi = orig.dpi || 72;
        const printW = ow / origDpi;
        const printH = oh / origDpi;
        tw = Math.round(printW * this.targetDpi);
        th = Math.round(printH * this.targetDpi);
      } else {
        return null;
      }

      return { ow, oh, tw, th };
    },

    targetSize() {
      const dims = this.resolveTargetDimensions();
      if (!dims) return '—';
      return `${dims.tw} × ${dims.th} px`;
    },

    printEstimate() {
      const orig = this.job?.original;
      if (!orig?.width || this.targetMode !== 'dpi') return '';
      const origDpi = orig.dpi || 72;
      const wIn = (orig.width / origDpi).toFixed(2);
      const hIn = (orig.height / origDpi).toFixed(2);
      return `Ukuran cetak ~ ${wIn}" × ${hIn}" @ ${this.targetDpi} DPI`;
    },

    engineHint() {
      if (this.processingMode === 'ai_enhance') return 'ai (Real-ESRGAN)';
      if (this.access?.ai_upscale?.available && this.job?.original?.width) {
        const mp = (this.job.original.width * this.job.original.height) / 1_000_000;
        const threshold = this.access.ai_upscale.auto_low_resolution_mp ?? 1;
        if (mp <= threshold) return 'ai (auto Real-ESRGAN)';
      }
      if (this.willUseLargeProcessing()) return 'tiled';
      if (this.processingMode === 'high_quality') return 'stepped';
      return 'local';
    },

    willUseLargeProcessing() {
      const dims = this.resolveTargetDimensions();
      if (!dims) return false;
      const threshold = 12_000_000;
      return dims.tw * dims.th >= threshold;
    },

    reset() {
      this.job = null;
      this.pdfPage = 1;
      this.pdfMode = 'single';
      this.selectedPdfPages = [];
      this.customWidth = null;
      this.customHeight = null;
      this.comparePos = 50;
      this.fullscreenOpen = false;
    },
  };
}

Alpine.data('inkoTarangTool', inkoTarangTool);
